import { useEffect, useState } from 'react';
import { Star, Quote } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface PublicReview {
  customer_name: string;
  therapist_name: string;
  rating: number;
  comment: string;
  created_at: string;
}
interface ReviewData {
  average: number;
  total: number;
  reviews: PublicReview[];
}

function Stars({ value, size = 'w-5 h-5' }: { value: number; size?: string }) {
  return (
    <div className="flex" aria-hidden>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${size} ${i <= Math.round(value) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );
}

const CustomerReviews = () => {
  const { t, language } = useLanguage();
  const [data, setData] = useState<ReviewData | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/reviews/public?limit=12`);
        if (res.ok && active) setData(await res.json());
      } catch {
        /* silently ignore — section simply won't render */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Inject real aggregateRating structured data for SEO once reviews are loaded
  useEffect(() => {
    if (!data || data.total === 0) return;
    const id = 'reviews-aggregate-jsonld';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'AggregateRating',
      itemReviewed: {
        '@type': 'HealthAndBeautyBusiness',
        name: 'Angel Massage',
        '@id': 'https://angel-massage.shop/#business',
      },
      ratingValue: data.average,
      reviewCount: data.total,
      bestRating: 5,
      worstRating: 1,
    });
  }, [data]);

  if (!data || data.total === 0) return null;

  const fmtDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(language === 'th' ? 'th-TH' : 'en-GB', {
        year: 'numeric',
        month: 'short',
      });
    } catch {
      return '';
    }
  };

  return (
    <section className="mt-16" data-testid="customer-reviews-section">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800">{t('reviews.title')}</h2>
        <p className="text-gray-600 mt-2">{t('reviews.subtitle')}</p>

        <div
          className="mt-5 inline-flex flex-col items-center gap-1 bg-white rounded-2xl px-8 py-5 shadow-lg border border-pink-100"
          data-testid="reviews-aggregate"
        >
          <div className="flex items-end gap-2">
            <span className="text-4xl font-extrabold text-pink-600" data-testid="reviews-average">
              {data.average.toFixed(1)}
            </span>
            <span className="text-sm text-gray-400 mb-1">{t('reviews.outOf5')}</span>
          </div>
          <Stars value={data.average} />
          <span className="text-sm text-gray-500 mt-1" data-testid="reviews-total">
            {t('reviews.basedOn')} {data.total} {data.total === 1 ? t('reviews.reviewWordSingular') : t('reviews.reviewsWord')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {data.reviews.map((r, idx) => (
          <div
            key={idx}
            className="relative bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
            data-testid={`review-card-${idx}`}
          >
            <Quote className="absolute top-4 right-4 w-8 h-8 text-pink-100" />
            <Stars value={r.rating} size="w-4 h-4" />
            {r.comment && <p className="text-gray-700 mt-3 leading-relaxed">&ldquo;{r.comment}&rdquo;</p>}
            <div className="mt-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-800">{r.customer_name}</p>
                {r.therapist_name && (
                  <p className="text-xs text-gray-400">
                    {t('reviews.withTherapist')} {r.therapist_name}
                  </p>
                )}
              </div>
              <span className="text-xs text-gray-400">{fmtDate(r.created_at)}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default CustomerReviews;
